import { BUDGET, mareaBudgetBp, PRESSURE, WAVE } from '@somnhold/content';
import { idiv, imax, imin, mulBp } from '../math/fixed.ts';
import type { CampaignState } from './types.ts';

/*
 * What a bastion wave is measured by (docs/SPEC.md §8.2): the heroes' level, never the calendar's Marea. The level
 * picks a step (the Marea whose creatures, plans, flanks, general and budget it calls for), and each campaign's
 * pressure, moved by what its real waves did, scales the budget from there.
 */

/** The heroes' mean level (bp of a level), those who left aside; 0 with nobody. */
const meanLevelBp = (s: CampaignState): number => {
  const active = s.players.filter((p) => !p.left);
  if (active.length === 0) return 0;
  let levels = 0;
  for (const p of active) levels += p.hero.level;
  return idiv(levels * 10000, active.length);
};

/**
 * The step the heroes' mean level calls for (WAVE.levelMarea; whole levels, rounded down; past the table, one more
 * every `levelMareaStep` levels): the Marea whose creatures, plans, flanks, general and budget a wave brings.
 */
export const levelMarea = (s: CampaignState): number => {
  if (!s.players.some((p) => !p.left)) return 0;
  const mean = idiv(meanLevelBp(s), 10000);
  const t = WAVE.levelMarea;
  const last = t.length - 1;
  if (mean >= (t[last] as number)) return last + idiv(mean - (t[last] as number), WAVE.levelMareaStep);
  let m = 0;
  for (let i = 1; i <= last; i++) if (mean >= (t[i] as number)) m = i;
  return m;
};

/** The mean hero level a step expects (WAVE.levelMarea, then `levelMareaStep` more per step). */
export const expectedLevel = (marea: number): number => {
  const t = WAVE.levelMarea;
  const last = t.length - 1;
  return marea <= last ? (t[marea < 0 ? 0 : marea] as number) : (t[last] as number) + (marea - last) * WAVE.levelMareaStep;
};

/** How many levels (bp) the heroes' mean is past the one step `marea` expects; 0 when it isn't. */
const levelsAhead = (s: CampaignState, marea: number): number => {
  if (!s.players.some((p) => !p.left)) return 0;
  const over = meanLevelBp(s) - expectedLevel(marea) * 10000;
  return over > 0 ? over : 0;
};

/** How much stronger a wave's nightmares are for heroes past the level its step expects (bp; 10000 when they aren't). */
export const levelStrength = (s: CampaignState, marea: number): number => 10000 + idiv(levelsAhead(s, marea) * WAVE.levelStrengthBp, 10000);

/** How much bigger a wave's budget is for heroes past the level its step expects (bp; 10000 when they aren't). */
export const levelBudget = (s: CampaignState, marea: number): number => 10000 + idiv(levelsAhead(s, marea) * WAVE.levelBudgetBp, 10000);

/**
 * The pressure a campaign starts from (bp): what its wave `wave` of Marea `marea` weighed by the calendar's rule
 * (its Marea's budget and ramp, and the level's count and strength past that Marea) over what the level's step
 * weighs, never under 1. So no wave got easier on the day the calendar stopped counting (2026-11-03).
 */
export const startingPressure = (s: CampaignState, marea: number, wave: number): number => {
  const step = levelMarea(s);
  const old = mulBp(mulBp(mulBp(mareaBudgetBp(marea), 10000 + wave * BUDGET.waveStepBp), levelBudget(s, marea)), levelStrength(s, marea));
  const now = mulBp(mulBp(mareaBudgetBp(step), levelBudget(s, step)), levelStrength(s, step));
  return imin(PRESSURE.maxBp, imax(10000, idiv(old * 10000, now > 0 ? now : 1)));
};

/** The campaign's pressure on its waves played live (`live`) or by its ghosts alone (bp). */
export const pressureOf = (s: CampaignState, live: boolean, marea: number, wave: number): number =>
  s.pressure ? (live ? s.pressure.live : s.pressure.ghost) : startingPressure(s, marea, wave);

/** After a wave: its pressure moves by what it did to the keep (`keepBp` of its life, bp), or by `lostBp` if it fell. */
export const pressureAfter = (p: number, lost: boolean, keepBp: number): number => {
  let stepBp = PRESSURE.lostBp;
  if (!lost) for (const st of PRESSURE.steps) if (keepBp < st.keepBp) {
    stepBp = st.stepBp;
    break;
  }
  return imin(PRESSURE.maxBp, imax(PRESSURE.minBp, mulBp(p, stepBp)));
};
